import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { reportBrokenMedia, resolveMedia, subscribeMedia, useMediaOffline } from '../../../../../capacitor/mediaCache';
import { useAppSelector } from '../../../../../store/hooks';
import { useGetMediaListQuery } from '../../overview/ducks/mediaDuck';

/** A row of the media list: the signed URLs are shown directly with `<img src>`. */
export type MediaEntry = { id: number; type: string; url: string; thumbnail_url: string };

/** The newest entry of `type`, by id. */
export const latestMedia = (mediaList: MediaEntry[] | undefined, type: string) =>
    (mediaList ?? []).reduce<MediaEntry | undefined>(
        (latest, media) => (media.type === type && (!latest || media.id > latest.id) ? media : latest),
        undefined,
    );

/**
 * When a signed media URL stops working, in Unix milliseconds, or null if it does not say. CloudFront
 * URLs carry `Expires` (Unix seconds); S3 presigned URLs carry `X-Amz-Date` plus `X-Amz-Expires`.
 */
export const mediaUrlExpiry = (url: string): number | null => {
    const params = new URL(url).searchParams;
    const expires = params.get('Expires');
    if (expires) return Number(expires) * 1000;
    const signedAt = params.get('X-Amz-Date')?.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
    const lifetime = params.get('X-Amz-Expires');
    if (!signedAt || !lifetime) return null;
    const [, year, month, day, hour, minute, second] = signedAt.map(Number);
    return Date.UTC(year, month - 1, day, hour, minute, second) + Number(lifetime) * 1000;
};

/**
 * Share of a signed URL's remaining life after which the list is fetched again, as chat does for
 * attachment URLs: early enough to absorb clock drift and request latency. CloudFront signs for at
 * least one full window, so the refetch always lands in a later window and brings a new URL.
 */
const REFRESH_AT = 0.8;

/** First wait after a failed refresh or image load; it doubles while it keeps failing, up to RETRY_MAX_MS. */
const RETRY_FIRST_MS = 30_000;
const RETRY_MAX_MS = 5 * 60_000;

/**
 * Fetch the media list again before `url` stops working, since a screen can stay mounted for longer
 * than a signed URL lives. A failed refetch leaves the old URL in place, so nothing else would try
 * again: it retries with backoff until one succeeds. A success brings the new URL, and the next
 * refresh is scheduled from it.
 */
export const useRefetchAtExpiry = (url: string | undefined, refetch: () => { unwrap(): Promise<unknown> }) => {
    useEffect(() => {
        const expiry = url ? mediaUrlExpiry(url) : null;
        if (expiry === null) return;
        let active = true;
        let timer: ReturnType<typeof setTimeout>;
        const attempt = (retryIn: number) => {
            refetch()
                .unwrap()
                .catch(() => {
                    if (!active) return;
                    timer = setTimeout(() => attempt(Math.min(retryIn * 2, RETRY_MAX_MS)), retryIn);
                });
        };
        timer = setTimeout(() => attempt(RETRY_FIRST_MS), Math.max(0, (expiry - Date.now()) * REFRESH_AT));
        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [url, refetch]);
};

// Image URLs that loaded once in this app session. A node image that mounts again, as the
// folded header's thumbnail does, shows at once instead of behind a loader.
const loadedSources = new Set<string>();

/**
 * The URL to show for media `id`, once its image at `url` has loaded, so callers keep a loader up
 * until the bytes are there; the `<img>` that shows it then reads the browser cache. When a refetch
 * brings a new URL for the same media, the shown one, still valid, stays until the new one loads,
 * and a failed load of the new one is retried with backoff: nothing else would try it again. With
 * nothing of the media shown yet, a failed load ends the wait on the failed URL, so the caller's
 * `<img>` shows the failure instead of a loader.
 *
 * In the app the image comes from the device media cache: the cached file, or after its download,
 * so it shows offline once seen. `keep` stores it with the files eviction never drops (thumbnails).
 * Offline with no cached file it is the remote URL, which fails as it would without the cache. A
 * cached file that fails to load is dropped and downloaded again.
 */
export const useLoadedImage = (id: number | undefined, url: string | undefined, options?: { keep?: boolean }) => {
    const keep = options?.keep === true;
    // A finished download renders again.
    const [, cacheChanged] = useReducer((n: number) => n + 1, 0);
    useEffect(() => subscribeMedia(cacheChanged), []);
    const offline = useMediaOffline();
    const source = url === undefined ? undefined : (resolveMedia(url, { keep }) ?? (offline ? url : undefined));
    const [shown, setShown] = useState<{ id: number; url: string } | undefined>(() =>
        id !== undefined && source !== undefined && loadedSources.has(source) ? { id, url: source } : undefined,
    );
    const shownRef = useRef(shown);
    shownRef.current = shown;
    useEffect(() => {
        if (id === undefined || source === undefined) return;
        if (shownRef.current?.id === id && shownRef.current.url === source) return;
        let image: HTMLImageElement;
        let timer: ReturnType<typeof setTimeout> | undefined;
        const attempt = (retryIn: number) => {
            image = new window.Image();
            image.onload = () => {
                loadedSources.add(source);
                setShown({ id, url: source });
            };
            image.onerror = () => {
                // A cached file: the cache drops it, downloads it again and renders this again.
                if (reportBrokenMedia(source)) return;
                if (shownRef.current?.id !== id) setShown({ id, url: source });
                else timer = setTimeout(() => attempt(Math.min(retryIn * 2, RETRY_MAX_MS)), retryIn);
            };
            image.src = source;
        };
        attempt(RETRY_FIRST_MS);
        return () => {
            clearTimeout(timer);
            image.onload = image.onerror = null;
        };
    }, [id, source]);
    const ready = id !== undefined && shown?.id === id;
    return { src: ready ? shown.url : undefined, loading: id !== undefined && !ready };
};

/**
 * Hook for a node's media (logo, machine photo, layout, etc.): the URL of the newest one of `type`.
 * By default it uses the currently selected node from Redux. Pass `nodeIdOverride`
 * to fetch media for a specific node — useful when rendering details for an
 * asset that is not the currently selected tree node (e.g. drawer with a sub-asset).
 */
const useMedia = (type: string, skip: boolean = false, nodeIdOverride?: number | null) => {
    const currentNode = useAppSelector((state) => state.nodeTreeReducers.currentNode);
    const effectiveNodeId = nodeIdOverride ?? currentNode?.id;

    // `currentData`, not `data`: after a node change the previous node's media must not linger.
    const {
        currentData: mediaList,
        isFetching,
        refetch,
    } = useGetMediaListQuery({ node_id: effectiveNodeId }, { skip: skip || effectiveNodeId == null });

    const latest = useMemo(() => latestMedia(mediaList, type), [mediaList, type]);
    useRefetchAtExpiry(latest?.url, refetch);
    const image = useLoadedImage(latest?.id, latest?.url);

    // A refetch of the same node keeps showing the current media instead of a loader.
    const isFetchingMedia = (isFetching && mediaList === undefined) || image.loading;
    return {
        fileId: latest?.id ?? 0,
        media: image.src ?? null,
        hasMedia: latest !== undefined,
        isFetchingMedia,
        isFetchingLogo: isFetchingMedia && type === 'user_logo',
    };
};

export default useMedia;
